import { useState, useCallback, useEffect, useMemo } from "react";
import { toast, Toaster } from "sonner";
import { AlertTriangle, RefreshCcw, FileDown } from "lucide-react";
import Header from "../components/Header";
import CVESearch from "../components/CVESearch";
import CVEMetadata from "../components/CVEMetadata";
import AIAnalysis from "../components/AIAnalysis";
import CodeDiff from "../components/CodeDiff";
import PatchPriority from "../components/PatchPriority";
import PseudoExploit from "../components/PseudoExploit";
import ExploitDifficulty from "../components/ExploitDifficulty";
import Mitigations from "../components/Mitigations";
import DetectionRules from "../components/DetectionRules";
import AssessmentKit from "../components/AssessmentKit";
import SupportBanner from "../components/SupportBanner";
import { AnalysisNav } from "../components/AnalysisNav";
import AnalysisLoader from "../components/AnalysisLoader";
import { analyzeCVEStream, exportCvePdfUrl } from "../lib/api";
import { bumpScanCount } from "../lib/scanCounter";

const CVE_RE = /^CVE-\d{4}-\d{4,7}$/i;

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentCve, setCurrentCve] = useState("");
  const [progress, setProgress] = useState({ steps: null, status: {} });

  const runAnalysis = useCallback(async (cveId) => {
    const id = (cveId || "").toUpperCase().trim();
    if (!CVE_RE.test(id)) {
      toast.error("Invalid CVE format. Use CVE-YYYY-NNNNN.");
      return;
    }
    setLoading(true);
    setCurrentCve(id);
    setData(null);
    setProgress({ steps: null, status: {} });
    const onEvent = (evt) => {
      if (evt.type === "steps") {
        setProgress((p) => ({ ...p, steps: evt.steps }));
      } else if (evt.type === "step") {
        setProgress((p) => ({
          ...p,
          status: { ...p.status, [evt.id]: { status: evt.status, reason: evt.reason } },
        }));
      }
    };
    try {
      const res = await analyzeCVEStream(id, false, onEvent);
      setData(res);
      if (res.ai_error) {
        toast.warning(`${res.id} - partial result`, {
          description: "NVD/MITRE data loaded; AI analysis is temporarily unavailable.",
        });
      } else {
        toast.success(`${res.id} analyzed`, {
          description: res.cached ? "Loaded from cache" : "Fresh analysis",
        });
      }
      // Trigger support-popup flow (counter + custom event).
      bumpScanCount();
      window.dispatchEvent(new CustomEvent("cve-scan-complete", { detail: { id: res.id } }));
    } catch (err) {
      const detail =
        err?.response?.data?.detail || err?.message || "Analysis failed";
      toast.error("Analysis failed", { description: String(detail) });
      setCurrentCve("");
    } finally {
      setLoading(false);
    }
  }, []);

  // Reset the dashboard to its landing state when the logo is clicked while
  // already on "/". Header.jsx dispatches the event so it works without
  // needing to thread a callback through the router.
  useEffect(() => {
    const reset = () => {
      setData(null);
      setCurrentCve("");
      setLoading(false);
    };
    window.addEventListener("app-home-reset", reset);
    return () => window.removeEventListener("app-home-reset", reset);
  }, []);

  // Auto-run analysis when arriving from /whats-broken with a ?cve= param.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cveParam = params.get("cve");
    if (cveParam) {
      runAnalysis(cveParam);
      // Strip the query so a refresh doesn't loop.
      window.history.replaceState({}, "", "/");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showHero = !data && !loading;
  const ai = data?.ai;
  const sectionIds = useMemo(() => {
    if (!data) return [];
    const has = Boolean(data.ai);
    return [
      "overview", "priority",
      ...(has ? ["rootcause", "exploitability", "pseudo", "assessment"] : []),
      "diff", "detection",
      ...(has ? ["mitigations"] : []),
    ];
  }, [data]);
  const scrollTopAfterLoad = data?.id;
  useEffect(() => {
    if (scrollTopAfterLoad) window.scrollTo({ top: 0 });
  }, [scrollTopAfterLoad]);

  return (
    <div className="App">
      <Toaster
        theme="light"
        position="bottom-right"
        toastOptions={{
          style: {
            background: "#ffffff",
            border: "1px solid #e2e8f0",
            color: "#0f172a",
          },
        }}
      />
      <Header showSearch={!showHero} onSearch={runAnalysis} loading={loading} />

      <main className="max-w-[1600px] mx-auto px-4 md:px-8 py-10 md:py-14 relative">
        {showHero ? (
          <div className="max-w-4xl mx-auto">
            <CVESearch onSubmit={runAnalysis} loading={loading} />
            <FeatureStrip />
          </div>
        ) : loading || !data ? (
          <div className="max-w-4xl mx-auto">
            <AnalysisLoader cveId={currentCve} steps={progress.steps} status={progress.status} />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[220px_minmax(0,1fr)] gap-6 lg:gap-10">
            <AnalysisNav
              cveId={data.id}
              ids={sectionIds}
              footer={<ExportButton cveId={data.id} full />}
            />
            <div className="min-w-0 space-y-6">
              <div className="lg:hidden flex justify-end">
                <ExportButton cveId={data.id} />
              </div>
              <Section id="overview"><CVEMetadata data={data} /></Section>
              {data.ai_error && <AIUnavailableBanner reason={data.ai_error} onRetry={() => runAnalysis(currentCve)} />}
              <Section id="priority"><PatchPriority ti={data.threat_intel} /></Section>
              {ai && (
                <>
                  <Section id="rootcause"><AIAnalysis ai={ai} /></Section>
                  <Section id="exploitability"><ExploitDifficulty ex={ai?.exploitability} /></Section>
                  <Section id="pseudo"><PseudoExploit pe={ai?.pseudo_exploit} /></Section>
                  <Section id="assessment"><AssessmentKit cveId={data.id} /></Section>
                </>
              )}
              <Section id="diff"><CodeDiff patches={data.patches} /></Section>
              <Section id="detection"><DetectionRules cveId={data.id} /></Section>
              {ai && <Section id="mitigations"><Mitigations items={ai?.mitigations} /></Section>}
            </div>
          </div>
        )}
        <SupportBanner />
        <Footer />
      </main>
    </div>
  );
}

function Section({ id, children }) {
  return (
    <section id={`sec-${id}`} data-testid={`section-${id}`}>
      {children}
    </section>
  );
}

function ExportButton({ cveId, full = false }) {
  const [busy, setBusy] = useState(false);
  const onClick = () => {
    if (busy) return;
    setBusy(true);
    toast.success(`Building ${cveId} PDF`, { description: "Download will start in a moment." });
    const a = document.createElement("a");
    a.href = exportCvePdfUrl(cveId);
    a.rel = "noreferrer";
    a.download = `${cveId}_cve-analytics.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.setTimeout(() => setBusy(false), 1500);
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      data-testid={full ? "export-pdf-btn" : "export-pdf-btn-mobile"}
      className={`${full ? "w-full justify-center" : ""} flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-slate-900 border border-slate-300 bg-white hover:bg-slate-50 rounded-md px-3 py-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
      title={`Download a PDF report for ${cveId}`}
    >
      <FileDown className="h-4 w-4" />
      {busy ? "Preparing..." : "Export PDF"}
    </button>
  );
}

function AIUnavailableBanner({ reason, onRetry }) {
  return (
    <div
      data-testid="ai-unavailable-banner"
    >
      <div className="flex items-start gap-3 flex-1">
        <div className="h-9 w-9 rounded-md bg-amber-50 border border-amber-200 grid place-items-center shrink-0">
          <AlertTriangle className="h-4 w-4 text-amber-700" />
        </div>
        <div>
          <div className="overline text-amber-700">AI analysis unavailable</div>
          <p className="text-sm text-slate-700 mt-1 leading-relaxed">
            NVD, MITRE, GitHub advisories, EPSS and KEV data are below. The
            AI walkthrough could not be generated right now (rate limit, key
            outage, or upstream timeout). Try again in a few seconds.
          </p>
          {reason && (
            <p className="text-[11px] font-mono text-slate-500 mt-1.5 break-all">
              {reason}
            </p>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={onRetry}
        data-testid="ai-retry-btn"
        className="flex items-center gap-1.5 text-xs font-mono text-amber-800 hover:text-amber-800 border border-amber-200 hover:border-amber-300 rounded-md px-3 py-2 transition-colors shrink-0"
      >
        <RefreshCcw className="h-3.5 w-3.5" />
        Retry AI
      </button>
    </div>
  );
}

function FeatureStrip() {
  const items = [
    { k: "NVD", v: "CVSS v3.1, weaknesses, affected products" },
    { k: "MITRE", v: "Authoritative CNA descriptions" },
    { k: "GITHUB", v: "Advisories + actual patch diff" },
    { k: "AI analysis", v: "Root cause, sanitized walkthrough and assessment kit" },
  ];
  return (
    <div className="mt-14 grid grid-cols-2 md:grid-cols-4 border-t border-slate-200">
      {items.map((it, i) => (
        <div
          key={it.k}
          className={`p-5 ${i > 0 ? "md:border-l border-slate-200" : ""} ${i > 1 ? "md:border-t-0 border-t border-slate-200 md:border-t-0" : ""}`}
        >
          <div className="overline mb-2">{it.k}</div>
          <div className="text-sm text-slate-700 leading-relaxed">{it.v}</div>
        </div>
      ))}
    </div>
  );
}

function Footer() {
  return (
    <footer className="mt-20 pt-8 border-t border-slate-100 flex flex-wrap items-center justify-between gap-4">
      <p className="text-xs text-slate-500 font-mono">
        Pseudo-exploits are intentionally non-functional. For research and
        education only.
      </p>
      <p className="text-xs text-slate-500 font-mono">
        Data: NVD · MITRE · GitHub Security Advisories · GitHub commits
      </p>
    </footer>
  );
}
